import React, { FC, useCallback, useEffect, useState } from 'react'
import { Badge, badgeClasses, Box, BoxProps, Button, ButtonProps, Checkbox } from '@mui/material'
import { FormControlLabel, List, ListItem, Popover, styled, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import { mergeObjects } from '../utils'
import HelpTooltip from './help-tooltip'

export type IMultiSelectDropdownFilterLogic = 'and' | 'or'

export interface IMultiSelectDropdownFilter {
  items: string[]
  logic?: IMultiSelectDropdownFilterLogic
}

export type IMultiSelectDropdownChange = (value: IMultiSelectDropdownFilter) => void

export interface IMultiSelectDropdownSlots {
  buttonProps?: ButtonProps
  contentPorps?: BoxProps
  defaultLogic?: IMultiSelectDropdownFilterLogic
}

export interface IMultiSelectDropdownProps {
  options?: string[]
  value?: IMultiSelectDropdownFilter
  onChange?: IMultiSelectDropdownChange
  slots?: IMultiSelectDropdownSlots
}

export interface IMultiSelectDropdownParams extends IMultiSelectDropdownSlots {
  btnText: string
  options?: string[]
  placeholder?: string
}

function CreateMultiSelectDropdown(params: IMultiSelectDropdownParams) {
  const MenuSelectMultiple: FC<IMultiSelectDropdownProps> = (props) => {
    const [search, setSearch] = useState<string>('')
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const [options, setOptions] = useState<string[]>(props.options ?? params.options ?? [])
    const [itemSelecteds, setItemSelecteds] = useState<string[]>([])
    const [logic, setLogic] = useState(props.slots?.defaultLogic ?? 'and')

    const mergeConfig = {
      buttonProps: mergeObjects<ButtonProps>(params?.buttonProps, props.slots?.buttonProps),
      contentProps: props.slots?.contentPorps ?? params?.contentPorps ?? {}
    }

    const getOptions = () => {
      const values = props.value?.items ?? []
      const list = options.filter((x) => x.toLowerCase().includes(search.toLowerCase())).sort((a, b) => a.localeCompare(b))

      const prioritized = list.filter((x) => values.includes(x))
      const others = list.filter((x) => !values.includes(x))

      return [...prioritized, ...others]
    }

    const setValueWithProps = useCallback(() => {
      if (props.value) {
        setLogic(props.value.logic ?? 'and')
        setItemSelecteds(props.value.items)
      }
    }, [props.value])

    const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget)
    }

    const handleClose = (event?: {}, reason?: 'backdropClick' | 'escapeKeyDown') => {
      setAnchorEl(null)
      setTimeout(setValueWithProps, 300)
    }

    const handleToggle = (value: string) => {
      setItemSelecteds((prev) => (prev.includes(value) ? prev.filter((t) => t !== value) : [...prev, value]))
    }

    const handleClear = () => {
      setItemSelecteds([])
      props.onChange && props.onChange({ items: [], logic })
    }

    const handleCancel = () => {
      setItemSelecteds([])
      handleClose()
    }

    const handleApply = () => {
      props.onChange && props.onChange({ items: itemSelecteds, logic })
      handleClose()
    }

    const handleChangeLogic = (_: React.MouseEvent<HTMLElement>, value: any) => {
      if (value) setLogic(value)
    }

    useEffect(() => {
      if (props.options && props.options.length > 0) setOptions(props.options)
    }, [props.options])

    useEffect(() => {
      setValueWithProps()
    }, [setValueWithProps])

    return (
      <>
        <Button color='inherit' endIcon={<FilterAltOutlinedIcon />} disabled={options.length < 1} {...mergeConfig.buttonProps} onClick={handleOpen}>
          {params.btnText}
          <ButtonBadge
            badgeContent={props.value?.items.length}
            color='error'
            overlap='circular'
            sx={{ transform: 'translateY(-50%) translateX(24px)' }}
          />
        </Button>
        <CustomPopover open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={handleClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
          <WrapActions className='top'>
            <TextField
              size='small'
              placeholder={params.placeholder ?? 'Search'}
              fullWidth
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <ToggleButtonGroup size='small' exclusive color='primary' value={logic} onChange={handleChangeLogic}>
              <ToggleButton value='and'>AND</ToggleButton>
              <ToggleButton value='or'>OR</ToggleButton>
            </ToggleButtonGroup>
            <HelpTooltip title='Filter Logic' small>
              <Typography variant='body2' color='text.secondary'>
                Choose how multiple categories are applied.
                <br />
                <strong> AND </strong> requires all selected categories, while
                <br />
                <strong> OR </strong> matches any.
              </Typography>
            </HelpTooltip>
          </WrapActions>
          <Box className='menu-content' {...mergeConfig.contentProps}>
            <List>
              {getOptions().map((x) => (
                <ListItem key={x} sx={{ whiteSpace: 'nowrap' }}>
                  <FormControlLabel label={x} control={<Checkbox checked={itemSelecteds.includes(x)} onChange={() => handleToggle(x)} />} />
                </ListItem>
              ))}
            </List>
          </Box>
          <WrapActions>
            <Button size='small' onClick={handleClear} color='inherit'>
              Clear
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button size='small' onClick={handleCancel} color='inherit'>
              Cancel
            </Button>
            <Button size='small' variant='contained' onClick={handleApply}>
              Apply
            </Button>
          </WrapActions>
        </CustomPopover>
      </>
    )
  }

  return MenuSelectMultiple
}

export default CreateMultiSelectDropdown

const heightItem = 58
const CustomPopover = styled(Popover)({
  '.MuiPaper-root': {
    overflow: 'hidden',
    position: 'relative',
    width: '360px'
  },
  '.menu-content': {
    overflowY: 'auto',
    maxHeight: `${heightItem * 5}px`,
    margin: '2px 2px 2px 0'
  }
})

const ButtonBadge = styled(Badge)({
  [`& .${badgeClasses.badge}`]: {
    top: '-12px',
    right: '-6px'
  }
})
const WrapActions = styled(Box)({
  alignItems: 'center',
  display: 'flex',
  gap: '6px',
  height: '58px',
  padding: '8px 10px',
  boxShadow: 'rgba(99, 99, 99, 0.2) 0px 2px 8px 0px'
})
